import React from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faPrint } from '@fortawesome/free-solid-svg-icons';

export interface ReportColumn<T = Record<string, unknown>> {
  key: string;
  header: string;
  formatter?: (value: unknown, row: T) => string;
}

export interface SearchCriterion {
  label: string;
  value: string;
}

interface ReportActionsProps<T = any> {
  data: T[];
  columns: ReportColumn<T>[];
  reportTitle: string;
  csvFileName?: string;
  searchCriteria?: SearchCriterion[];
  onExport?: () => void;
  onPrint?: () => void;
  disabled?: boolean;
  size?: 'sm' | 'lg';
  variant?: 'primary' | 'secondary' | 'outline-primary' | 'outline-secondary';
  footerHtml?: string;
  csvColumns?: ReportColumn<T>[];
}

export const ReportActions = <T,>({
  data,
  columns,
  reportTitle,
  csvFileName,
  searchCriteria,
  onExport,
  onPrint,
  disabled = false,
  size = 'sm',
  variant = 'outline-primary',
  footerHtml,
  csvColumns
}: ReportActionsProps<T>) => {
  const handleExport = () => {
    if (onExport) {
      onExport();
      return;
    }

    // Default CSV export functionality
    exportToCsv(
      data,
      csvColumns || columns,
      csvFileName || `${reportTitle.replace(/\s+/g, '')}.csv`
    );
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
      return;
    }

    // Default print functionality
    printReport(data, columns, reportTitle, searchCriteria, footerHtml);
  };

  return (
    <div className="d-flex gap-2">
      <Button
        variant={variant}
        size={size}
        onClick={handleExport}
        disabled={disabled || data.length === 0}>
        <FontAwesomeIcon icon={faFileExport} className="me-1" />
        Export CSV
      </Button>
      <Button
        variant={variant}
        size={size}
        onClick={handlePrint}
        disabled={disabled || data.length === 0}>
        <FontAwesomeIcon icon={faPrint} className="me-1" />
        Print All
      </Button>
    </div>
  );
};

// CSV Export Utility Function
const exportToCsv = <T,>(data: T[], columns: ReportColumn<T>[], fileName: string) => {
  // Create CSV headers
  const headers = columns.map((col) => col.header);

  // Create CSV rows
  const csvRows = data.map((row) => {
    return columns.map((col) => {
      const value = getNestedValue(row, col.key);
      const formattedValue = col.formatter ? col.formatter(value, row) : value;

      // Handle null/undefined values
      if (formattedValue === null || formattedValue === undefined) {
        return '';
      }

      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      const stringValue = String(formattedValue);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    });
  });

  // Combine headers and rows
  const csvContent = [headers, ...csvRows].map((row) => row.join(',')).join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Print Utility Function
const printReport = <T,>(
  data: T[],
  columns: ReportColumn<T>[],
  reportTitle: string,
  searchCriteria?: SearchCriterion[],
  footerHtml?: string
) => {
  // Generate search criteria HTML
  const searchCriteriaHtml =
    searchCriteria && searchCriteria.length > 0
      ? `
    <div class="search-criteria">
      <h3>Search Criteria</h3>
      <div class="criteria-items">
        ${searchCriteria
          .map(
            (criterion) =>
              `<span class="criterion"><strong>${criterion.label}:</strong> ${criterion.value}</span>`
          )
          .join('')}
      </div>
    </div>
  `
      : '';

  // Generate table HTML
  const tableHeaders = columns.map((col) => `<th>${col.header}</th>`).join('');
  const tableRows = data
    .map((row) => {
      const cells = columns
        .map((col) => {
          const value = getNestedValue(row, col.key);
          const formattedValue = col.formatter ? col.formatter(value, row) : value;
          return `<td>${String(formattedValue || '')}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  const printHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            font-size: 12px;
          }

          h1 {
            font-size: 18px;
            margin-bottom: 10px;
            text-align: center;
          }

          .search-criteria {
            margin-bottom: 20px;
            padding: 10px;
            background-color: #f8f9fa;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
          }

          .search-criteria h3 {
            font-size: 14px;
            margin: 0 0 8px 0;
            color: #333;
          }

          .criteria-items {
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
          }

          .criterion {
            font-size: 11px;
            color: #555;
          }

          .criterion strong {
            color: #333;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }

          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }

          th {
            background-color: #f5f5f5;
            font-weight: bold;
          }

          tr:nth-child(even) {
            background-color: #f9f9f9;
          }

          @media print {
            body {
              margin: 0;
            }

            .search-criteria {
              margin-bottom: 15px;
              padding: 8px;
            }

            .search-criteria h3 {
              font-size: 12px;
              margin-bottom: 6px;
            }

            .criteria-items {
              gap: 12px;
            }

            .criterion {
              font-size: 10px;
            }

            table {
              font-size: 10px;
            }

            th, td {
              padding: 4px;
            }
          }

          .report-footer {
            margin-top: 30px;
            border-top: 2px solid #333;
            padding-top: 10px;
          }

          .report-footer h3 {
            font-size: 14px;
            margin-bottom: 10px;
          }

          .footer-totals-table {
            width: auto;
            min-width: 500px;
            margin-top: 0;
          }

          .footer-totals-table th {
            background-color: #f8f9fa;
            text-align: center;
          }

          .footer-totals-table td {
            text-align: center;
            font-size: 14px;
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <h1>${reportTitle}</h1>
        ${searchCriteriaHtml}
        <table>
          <thead>
            <tr>${tableHeaders}</tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        ${footerHtml ? `<div class="report-footer">${footerHtml}</div>` : ''}
      </body>
    </html>
  `;

  // Create a hidden iframe instead of opening a new window
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.top = '-9999px';
  iframe.style.left = '-9999px';
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.visibility = 'hidden';

  // Add the iframe to the document
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDoc) {
    document.body.removeChild(iframe);
    return;
  }

  // Write the print content to the iframe
  iframeDoc.write(printHtml);
  iframeDoc.close();

  // Wait for the iframe to load, then trigger print
  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();

      // Clean up the iframe after printing starts
      // Note: We can't reliably detect when printing is done, so we clean up after a delay
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 1000);
    } catch (error) {
      console.error('Print failed:', error);
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }
  };

  // Fallback cleanup in case onload doesn't fire
  setTimeout(() => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  }, 5000);
};

// Utility function to get nested object values (e.g., "committee.description")
const getNestedValue = <T,>(obj: T, path: string): unknown => {
  return path.split('.').reduce((current: unknown, key: string) => {
    if (current && typeof current === 'object' && key in current) {
      return (current as Record<string, unknown>)[key];
    }
    return null;
  }, obj);
};
